import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { useAdministrationScopes } from '../place/AdministrationScopesProvider';
import { PlaceChooser } from '../place/PlaceChooser';
import { sectionPath } from '../place/place';

/**
 * `/admin/platform`. The global administrator reaches people by choosing a tenant or an
 * organization (FR-GA-5: no platform-wide list of people), so the platform's page is the chooser.
 * Beside it, the way to Tenant, where tenants and their first organizations are created (slice 2).
 * Today's operator screens are retired (FR-AD-12): their old addresses lead to the new places.
 */
export function PlatformHome() {
  const { t } = useTranslation('administration');
  const { scopes } = useAdministrationScopes();
  const canBrowseTenants = scopes.some(
    (scope) => scope.tier === 'global' && scope.can.browseTenants,
  );

  return (
    <section aria-labelledby="platform-home-title" className="flex max-w-[640px] flex-col gap-4">
      <div>
        <h2 id="platform-home-title" className="screen-title">
          {t('picker.title')}
        </h2>
        <p className="screen-summary">{t('picker.hint')}</p>
      </div>
      <div className="border-border bg-card rounded-xl border p-3">
        <PlaceChooser current={{ kind: 'platform' }} />
      </div>
      {canBrowseTenants ? (
        <PlatformLink
          title={t('platform.tenantsTitle')}
          text={t('platform.tenantsText')}
          to={sectionPath({ kind: 'platform' }, 'tenants')}
          label={t('platform.tenantsLink')}
        />
      ) : null}
    </section>
  );
}

function PlatformLink({
  title,
  text,
  to,
  label,
}: {
  readonly title: string;
  readonly text: string;
  readonly to: string;
  readonly label: string;
}) {
  return (
    <div className="border-border bg-card flex flex-wrap items-center justify-between gap-3 rounded-xl border p-4">
      <div className="min-w-0">
        <h3 className="text-foreground text-[15px] font-semibold">{title}</h3>
        <p className="text-muted-foreground mt-1 text-sm">{text}</p>
      </div>
      <Button asChild variant="outline">
        <Link to={to}>
          {label}
          <ArrowRight />
        </Link>
      </Button>
    </div>
  );
}
